import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Bell, DollarSign, Heart, Flame, Trophy, Eye, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { onRealtimeEvent } from "@/lib/realtime";
import { fireHeartsConfetti, fireCoinsConfetti, fireBoostConfetti, fireDiamondsConfetti, fireTrophyConfetti } from "@/lib/confetti";
import { useLocale } from "@/i18n/LocaleContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function NotificationBell({ testId = "notification-bell-btn" }) {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { t } = useLocale();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const timeAgo = (iso) => {
    const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 60) return t("time.now");
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t("time.m", { n: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("time.h", { n: hours });
    return t("time.d", { n: Math.floor(hours / 24) });
  };

  const resolveNotif = (notif) => {
    if (notif.tkey && t(`${notif.tkey}.t`) !== `${notif.tkey}.t`) {
      const params = {};
      Object.entries(notif.tparams || {}).forEach(([k, v]) => {
        params[k] = typeof v === "string" && v.startsWith("@") ? t(v.slice(1)) : v;
      });
      return { title: t(`${notif.tkey}.t`, params), body: t(`${notif.tkey}.b`, params) };
    }
    return { title: notif.title, body: notif.body };
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get("/notifications");
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      // silent - non-critical
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const unsubscribe = onRealtimeEvent((event) => {
      if (event.type === "notification" && event.notification) {
        setNotifications((prev) => [event.notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        const { title, body } = resolveNotif(event.notification);
        toast.success(title, { description: body });
        if (["payment_escrowed", "offer_paid"].includes(event.notification.type)) {
          fireHeartsConfetti();
        }
        if (event.notification.type === "credits_added") {
          fireCoinsConfetti();
          checkAuth();
        }
        if (event.notification.type === "boost_activated") {
          fireBoostConfetti();
        }
        if (event.notification.type === "badge_earned") {
          fireDiamondsConfetti();
        }
        if (event.notification.type === "leaderboard_rank") {
          fireTrophyConfetti();
        }
      }
    });
    return unsubscribe;
  }, []);

  const handleOpenChange = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && unreadCount > 0) {
      try {
        await api.post("/notifications/mark-read", {});
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch (error) {
        // silent
      }
    }
  };

  const handleNotificationClick = (notif) => {
    setOpen(false);
    if (notif.type === "payment_escrowed") {
      navigate("/premium-offers");
    } else if ((notif.type === "social_invite_joined" || notif.type === "secret_admirer_reveal") && notif.data?.user_id) {
      navigate(`/profile/${notif.data.user_id}`);
    } else if (notif.type === "leaderboard_rank") {
      navigate("/leaderboard");
    } else if (notif.type === "match_insights") {
      navigate("/discover");
    }
  };

  const notifIcon = (type) => {
    if (type === "payment_escrowed") return { bg: "bg-green-500/15", icon: <DollarSign className="w-4 h-4 text-green-400" /> };
    if (type === "streak_progress") return { bg: "bg-orange-500/15", icon: <Flame className="w-4 h-4 text-orange-400" /> };
    if (type === "leaderboard_rank") return { bg: "bg-amber-500/15", icon: <Trophy className="w-4 h-4 text-amber-400" /> };
    if (type === "match_insights") return { bg: "bg-violet-500/15", icon: <Eye className="w-4 h-4 text-violet-400" /> };
    if (type === "support_resolved") return { bg: "bg-green-500/15", icon: <BadgeCheck className="w-4 h-4 text-green-400" /> };
    return { bg: "bg-green-500/15", icon: <Heart className="w-4 h-4 text-primary" /> };
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
          data-testid={testId}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 bg-[#1a1226] border-white/10 max-h-[420px] overflow-y-auto"
        data-testid="notifications-list"
      >
        <div className="px-4 py-3 border-b border-white/10">
          <h3 className="font-semibold text-sm">{t("nb.title")}</h3>
        </div>
        {notifications.length === 0 ? (
          <div className="px-4 py-10 text-center text-muted-foreground text-sm">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {t("nb.empty")}
          </div>
        ) : (
          notifications.map((notif) => {
            const { title, body } = resolveNotif(notif);
            return (
              <button
                key={notif.notification_id}
                onClick={() => handleNotificationClick(notif)}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                  !notif.read ? "bg-primary/5" : ""
                }`}
                data-testid={`notification-item-${notif.notification_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full ${notifIcon(notif.type).bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    {notifIcon(notif.type).icon}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-snug">{title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{body}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1">{timeAgo(notif.created_at)}</p>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
